import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
// import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import useAuth from '../hooks/useAuth';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN, PATH_RETAILER_OUTLET_AFTER_LOGIN, PATH_RETAILER_HQ_AFTER_LOGIN } from '../config';
// components
import LoadingScreen from '../components/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

// Distinguish between the RetailerHQ and RetailerOutlet
const RetailerHQRetailerOutletRedirect = () => {
  const { user } = useAuth();

  if (user.isRetailerHQ) {
    // <Navigate to={PATH_AFTER_LOGIN} replace />
    return <Navigate to={PATH_RETAILER_HQ_AFTER_LOGIN} />;
  }

  return <Navigate to={PATH_RETAILER_OUTLET_AFTER_LOGIN} />;
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'new-password', element: <NewPassword /> },
        { path: 'verify', element: <VerifyCode /> },
        { path: 'request-sent', element: <RequestSent /> },
      ],
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <RetailerHQRetailerOutletRedirect />, index: true },
        {
          path: 'retailer-hq',
          children: [
            { element: <Navigate to="/dashboard/retailer-hq/home#deliveriesSummaryReport" replace />, index: true },
            { path: 'home', element: <BranchesDeliveries /> },
            { path: 'all-deliveries', element: <ExpandedDeliveries /> },
            { path: 'report', element: <ReportDeliveries /> },
            { path: 'my-suppliers', element: <MySuppliers /> },
          ],
        },
        {
          path: 'suppliers',
          children: [{ path: 'retailer/suppliers', element: <MySuppliers /> }],
        },
        {
          path: 'users',
          children: [
            { path: 'retailer/list', element: <RetailerUserList /> },
            { path: 'retailer/new', element: <RetailerUserCreate /> },
            { path: 'retailer/account', element: <RetailerUserAccount /> },
            // { path: 'retailer/:id/edit', element: <RetailerUserCreate /> }
          ],
        },
      ],
      // children: [
      //   { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
      //   {
      //     path: 'retailers',
      //     children: [
      //       { path: 'delivery-summary', element: <DeliverySummary /> },
      //       { path: 'all-deliveries', element: <AllDeliveries /> },
      //       { path: 'analytics', element: <Analytics /> },
      //     ],
      //   },
      //   {
      //     path: 'users',
      //     children: [
      //       { path: 'retailer/list', element: <RetailerUserList /> },
      //       { path: 'retailer/new', element: <RetailerUserCreate /> },
      //       { path: 'retailer/account', element: <RetailerUserAccount /> },
      //       { path: 'retailer/:id/edit', element: <RetailerUserCreate /> }
      //     ]
      //   }
      // ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: 'pricing', element: <Pricing /> },
        { path: 'payment', element: <Payment /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      children: [
        {
          index: true,
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        { path: 'home', element: <HomePage /> },
        { path: 'about-us', element: <About /> },
        { path: 'contact-us', element: <Contact /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// Retailer HQ
const BranchesDeliveries = Loadable(lazy(() => import('../pages/dashboard/retailerHQ/BranchesDeliveries')));

// Retailer
const DeliverySummary = Loadable(lazy(() => import('../pages/dashboard/retailers/deliveryTracking/DeliverySummary')));
// const AllDeliveries = Loadable(lazy(() => import('../pages/dashboard/retailers/deliveryTracking/AllDeliveries'))); TODO Delete
const ExpandedDeliveries = Loadable(
  lazy(() => import('../pages/dashboard/retailers/deliveryTracking/ExpandedDeliveries'))
);
const ReportDeliveries = Loadable(lazy(() => import('../pages/dashboard/retailers/deliveryTracking/ReportDeliveries')));
const MySuppliers = Loadable(lazy(() => import('../pages/dashboard/retailers/deliveryTracking/MySuppliers')));
const Analytics = Loadable(lazy(() => import('../pages/dashboard/retailers/analytics/Analytics')));
const RetailerUserList = Loadable(lazy(() => import('../pages/dashboard/retailers/user/RetailerUserList')));
const RetailerUserCreate = Loadable(lazy(() => import('../pages/dashboard/retailers/user/RetailerUserCreate')));
const RetailerUserAccount = Loadable(lazy(() => import('../pages/dashboard/retailers/user/RetailerUserAccount')));

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));
const RequestSent = Loadable(lazy(() => import('../pages/auth/RequestSent')));

// MAIN
const HomePage = Loadable(lazy(() => import('../pages/Home')));
const About = Loadable(lazy(() => import('../pages/About')));
const Contact = Loadable(lazy(() => import('../pages/Contact')));
// const Faqs = Loadable(lazy(() => import('../pages/Faqs')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Pricing = Loadable(lazy(() => import('../pages/Pricing')));
const Payment = Loadable(lazy(() => import('../pages/Payment')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));

// const ModelInferencing = Loadable(lazy(() => import('../pages/dashboard/ModelInferencing')))
// const PickupSchedule = Loadable(lazy(() => import('../pages/dashboard/PickupSchedule')))
// const GuestOrdersDeliveryView = Loadable(lazy(() => import('../pages/dashboard/GuestOrdersDeliveryView')))
// const GuestOrderDeliveryView = Loadable(lazy(() => import('../pages/dashboard/GuestOrderDeliveryView')))
// const ShipperWelcome = Loadable(lazy(() => import('../pages/ShipperWelcome')));
// const Acquisition = Loadable(lazy(() => import('../pages/onboarding/Acquisition')));

// DASHBOARD

// GENERAL
// const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
// const GeneralEcommerce = Loadable(lazy(() => import('../pages/dashboard/GeneralEcommerce')));
// const GeneralAnalytics = Loadable(lazy(() => import('../pages/dashboard/GeneralAnalytics')));
// const GeneralBanking = Loadable(lazy(() => import('../pages/dashboard/GeneralBanking')));
// const GeneralBooking = Loadable(lazy(() => import('../pages/dashboard/GeneralBooking')));

// ECOMMERCE
// const EcommerceShop = Loadable(lazy(() => import('../pages/dashboard/EcommerceShop')));
// const EcommerceProductDetails = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductDetails')));
// const EcommerceProductList = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductList')));
// const EcommerceProductCreate = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductCreate')));
// const EcommerceProductEdit = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductEdit')));
// const EcommerceCheckout = Loadable(lazy(() => import('../pages/dashboard/EcommerceCheckout')));

// INVOICE
// const InvoiceList = Loadable(lazy(() => import('../pages/dashboard/InvoiceList')));
// const InvoiceDetails = Loadable(lazy(() => import('../pages/dashboard/InvoiceDetails')));
// const InvoiceCreate = Loadable(lazy(() => import('../pages/dashboard/InvoiceCreate')));
// const InvoiceEdit = Loadable(lazy(() => import('../pages/dashboard/InvoiceEdit')));

// DELIVERY NOTE
// const DeliveryNoteDetails = Loadable(lazy(() => import('../pages/dashboard/DeliveryNoteDetails')));
// const DnoteEdit = Loadable(lazy(() => import('../pages/dashboard/DnoteEdit')));

// BLOG
// const BlogPosts = Loadable(lazy(() => import('../pages/dashboard/BlogPosts')));
// const BlogPost = Loadable(lazy(() => import('../pages/dashboard/BlogPost')));
// const BlogNewPost = Loadable(lazy(() => import('../pages/dashboard/BlogNewPost')));

// USER
// const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
// const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')));
// const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
// const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
// const UserCreate = Loadable(lazy(() => import('../pages/dashboard/shipper/UserCreate')));
// const UserSettings = Loadable(lazy(() => import('../pages/dashboard/shipper/UserSettings')));
// const ShipperProfile = Loadable(lazy(() => import('../pages/dashboard/shipper/ShipperProfile')));

// APP
// const Chat = Loadable(lazy(() => import('../pages/dashboard/shipper/Chat')));
// const Mail = Loadable(lazy(() => import('../pages/dashboard/shipper/Mail')));
// const Calendar = Loadable(lazy(() => import('../pages/dashboard/shipper/Calendar')));
// const Kanban = Loadable(lazy(() => import('../pages/dashboard/shipper/Kanban')));

// TEST RENDER PAGE BY ROLE
// const PermissionDenied = Loadable(lazy(() => import('../pages/dashboard/shipper/PermissionDenied')));

// Transportation Management System

// Dashboard
// const Invoicing = Loadable(lazy(() => import('../pages/dashboard/Invoicing')));
// const Returns = Loadable(lazy(() => import('../pages/dashboard/Returns')));
// const ReturnDeliveries = Loadable(lazy(() => import('../pages/dashboard/ReturnDeliveries')));
// const ScheduleReturnsPage = Loadable(lazy(() => import('../pages/dashboard/ScheduleReturnsPage')));
// const ReturnNoteDetails = Loadable(lazy(() => import('../pages/dashboard/ReturnNoteDetails')));
// const ReturnItems = Loadable(lazy(() => import('../pages/dashboard/ReturnItems')));
// const OnboardShipper = Loadable(lazy(() => import('../pages/dashboard/OnboardShipper')));
// const SchedulePickup = Loadable(lazy(() => import('../pages/dashboard/SchedulePickup')));
// const SchedulePickupEdit = Loadable(lazy(() => import('../pages/dashboard/SchedulePickupEdit')));
// const PackagingView = Loadable(lazy(() => import('../pages/dashboard/Packaging')));
// const BillingInfo = Loadable(lazy(() => import('../pages/dashboard/BillingInfo')));
// const InvoiceList = Loadable(lazy(() => import('../pages/dashboard/InvoiceList')));
// const DeliveriesTracking = Loadable(lazy(() => import('../pages/dashboard/DeliveriesTracking')));
// const DeliveryTracking = Loadable(lazy(() => import('../pages/dashboard/DeliveryTracking')));
// const DeliveryNotes = Loadable(lazy(() => import('../pages/dashboard/DeliveryNotes')));
// const GenerateDeliveryNotes = Loadable(lazy(() => import('../pages/dashboard/GenerateDnote')));
// const CustomerGraph = Loadable(lazy(() => import('../pages/dashboard/CustomerGraph')));
// const Feedback = Loadable(lazy(() => import('../pages/dashboard/Feedback')));
// const Worksheet = Loadable(lazy(() => import('../pages/dashboard/Worksheet')));
// const PickupRequests = Loadable(lazy(() => import('../pages/dashboard/PickupRequests')));
